import { useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { EhDataTableShell } from "@/components/dataTable/EhDataTableShell";
import {
  FLOATING_TABLE_HEADER_Z_CLASS,
  useFloatingProjectTableHeader,
} from "@/pages/projekte/useFloatingProjectTableHeader";

export interface DataTableScrollShellProps {
  /** Änderungen an Spalten/Zeilen → Floating-Header neu messen. */
  syncKey: string;
  stickyHeader?: boolean;
  className?: string;
  tableClassName?: string;
  /** Inhalt von `<thead>` (typisch eine `<tr>…</tr>`). */
  head: ReactNode;
  children: ReactNode;
}

/**
 * Datentabelle mit schwebendem Kopf beim Seiten-Scroll (wie Projekte/Stundennachweise).
 * CSS `position: sticky` auf `th` funktioniert nicht zuverlässig innerhalb von
 * `overflow-x-auto` – daher fixierter Header-Klon per Portal.
 */
export function DataTableScrollShell({
  syncKey,
  stickyHeader = true,
  className,
  tableClassName,
  head,
  children,
}: DataTableScrollShellProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const floating = useFloatingProjectTableHeader(wrapRef, tableRef, {
    enabled: stickyHeader,
    syncKey,
  });

  const theadClass = clsx(
    "eh-data-thead bg-card-elevated text-xs uppercase tracking-wide text-label-secondary",
  );

  return (
    <>
      <EhDataTableShell ref={wrapRef} className={className}>
        <table
          ref={tableRef}
          className={clsx("eh-data-table min-w-full divide-y divide-separator text-sm", tableClassName)}
        >
          <thead className={theadClass}>{head}</thead>
          {children}
        </table>
      </EhDataTableShell>

      {stickyHeader &&
        floating &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`pointer-events-none fixed ${FLOATING_TABLE_HEADER_Z_CLASS} overflow-hidden rounded-b-ios border border-t-0 border-separator bg-card-elevated shadow-[0_8px_18px_rgba(15,23,42,0.22)]`}
            style={{
              top: floating.top,
              left: floating.left,
              width: floating.width,
            }}
            aria-hidden
          >
            <div
              className="pointer-events-auto eh-floating-table-header-sync"
              style={{
                width: floating.tableWidth,
                transform: `translateX(${-floating.scrollLeft}px)`,
                ["--eh-table-scroll-left" as string]: `${floating.scrollLeft}px`,
              }}
            >
              <table className="eh-data-table w-full text-sm">
                <thead className={theadClass}>{head}</thead>
              </table>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
